// ─────────────────────────────────────────────────────────────────────────
// Creative Strategist Results tab (Task #2382, spec §55–§60).
//
// The page a user lands on when the project is done. Everything above the
// narrative is COUNTED from project data — the categories, the per-initiative
// lists, the decisions and their provenance. Only the six prose sections come
// from the model, and when that call fails the page still renders with a
// plainer summary rather than showing nothing.
//
// That is the point of the spec's "do not merely summarize the chat": a
// number here is a number we counted.
// ─────────────────────────────────────────────────────────────────────────

import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Rocket,
  Sparkles,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  CS_DECISION_SOURCE_CLASSES,
  CS_DECISION_SOURCE_LABELS,
  csFormatDateTime,
  csResultsKey,
  type CreativeStrategistResultsState,
} from "@/lib/creative-strategist";

interface Props {
  projectId: string;
  brandId: string | undefined;
  onOpenDeliverables: () => void;
}

export function CreativeStrategistResultsTab({
  projectId,
  brandId,
  onOpenDeliverables,
}: Props) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data, isLoading } = useQuery<CreativeStrategistResultsState>({
    queryKey: [csResultsKey(projectId)],
    enabled: !!projectId,
    // While work is still in flight the answer changes; once complete it does
    // not, so this only matters for the tail of a running project.
    refetchInterval: (query) =>
      (query.state.data as CreativeStrategistResultsState | undefined)?.results ? false : 30_000,
  });

  const generate = useMutation({
    mutationFn: async () => {
      await apiRequest(
        "POST",
        `/api/creative-strategist/projects/${projectId}/results/generate`,
        {},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [csResultsKey(projectId)] });
    },
    onError: (e: any) =>
      toast({
        title: "Could not build Results",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  const buildNext = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/creative-strategist/projects/${projectId}/build-next`,
        {},
      );
      return (await res.json()) as { id: string; title: string };
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/brands/${brandId}/projects`],
      });
      navigate(`/brands/${brandId}/creative-strategist/projects/${project.id}`);
    },
    onError: (e: any) =>
      toast({
        title: "Could not start the next project",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const results = data?.results ?? null;

  if (!results) {
    const completion = data?.completion;
    return (
      <Card>
        <CardContent className="py-14 text-center space-y-3" data-testid="empty-results">
          <Sparkles className="h-6 w-6 mx-auto text-muted-foreground" />
          <div className="text-sm font-medium">
            {data?.status === "generating" ? "Putting Results together…" : "No results yet"}
          </div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {completion?.reason ??
              "Everything the project produced will be summarized here once the work is finished."}
          </p>
          {completion && !completion.complete && (
            <p className="text-xs text-muted-foreground">
              {completion.completed} done · {completion.activeTasks} running ·{" "}
              {completion.awaitingUser} waiting on you
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
            data-testid="button-generate-results"
          >
            {generate.isPending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1.5" />
            )}
            Build Results now
          </Button>
        </CardContent>
      </Card>
    );
  }

  const n = results.narrative;

  return (
    <div className="space-y-6" data-testid="results-page">
      {/* Header (spec §56) */}
      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-xl font-bold tracking-tight" data-testid="text-results-headline">
                {n.headline || results.projectTitle}
              </h2>
              <Badge variant="secondary" className="mt-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                Complete
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {results.generatedAt ? csFormatDateTime(results.generatedAt) : ""}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Here's everything Creative Strategist completed for you.
          </p>
        </CardContent>
      </Card>

      {/* What Was Completed (spec §56) */}
      {results.completedCategories.length > 0 && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              What was completed
            </div>
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
              data-testid="results-completed-categories"
            >
              {results.completedCategories.map((c) => (
                <div key={c.category} data-testid={`results-category-${c.category}`}>
                  <div className="text-2xl font-bold tabular-nums">{c.count}</div>
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {c.breakdown.map((b) => `${b.count} ${b.label}`).join(", ")}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouped by initiative (spec §57) */}
      {results.initiatives.length > 0 && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Results by initiative
            </div>
            <div className="space-y-4" data-testid="results-initiatives">
              {results.initiatives.map((initiative) => (
                <div key={initiative.key} className="space-y-1.5">
                  <div className="text-sm font-semibold">{initiative.title}</div>
                  <ul className="space-y-1">
                    {initiative.items.map((item) => (
                      <li key={item.deliverableId}>
                        <button
                          type="button"
                          onClick={onOpenDeliverables}
                          className="text-xs text-left inline-flex items-center gap-1.5 hover:text-foreground text-muted-foreground transition-colors"
                          data-testid={`results-item-${item.deliverableId}`}
                        >
                          <ArrowUpRight className="h-3 w-3 shrink-0" />
                          <span className="font-medium text-foreground">{item.title}</span>
                          <span>· {item.typeLabel}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={onOpenDeliverables}
              data-testid="button-open-deliverables-from-results"
            >
              Open the deliverables library
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Decisions with provenance (spec §58) */}
      {results.decisions.length > 0 && (
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Major decisions
            </div>
            <div className="space-y-2" data-testid="results-decisions">
              {results.decisions.map((d) => (
                <div
                  key={d.id}
                  className="flex items-start justify-between gap-3 border-b pb-2 last:border-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{d.title}</div>
                    {d.rationale && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{d.rationale}</p>
                    )}
                  </div>
                  <Badge
                    variant="secondary"
                    className={`shrink-0 ${CS_DECISION_SOURCE_CLASSES[d.source] ?? ""}`}
                    data-testid={`results-decision-source-${d.id}`}
                  >
                    {CS_DECISION_SOURCE_LABELS[d.source] ?? d.source}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project summary (spec §59) */}
      <Card>
        <CardContent className="p-6 space-y-5">
          {!n.generated && (
            <p className="text-[11px] text-muted-foreground">
              The written summary could not be generated, so this is built straight from the
              project's data.
            </p>
          )}
          <Section title="Goal" body={n.goal} testId="results-goal" />
          <Section title="What we built" body={n.whatWeBuilt} testId="results-what-we-built" />
          <ListSection
            title="Major decisions"
            items={n.majorDecisions}
            testId="results-major-decisions"
          />
          <ListSection
            title="Key learnings"
            items={n.keyLearnings}
            testId="results-key-learnings"
          />
          <ListSection
            title="Outstanding items"
            items={
              n.outstandingItems.length > 0
                ? n.outstandingItems
                : results.outstanding.map((o) => `${o.title} — ${o.reason}`)
            }
            testId="results-outstanding"
            icon={AlertTriangle}
          />

          {n.recommendedNextWork && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Recommended next work
                </div>
                <div className="text-sm font-semibold">{n.recommendedNextWork.title}</div>
                <p className="text-sm text-muted-foreground">
                  {n.recommendedNextWork.objective}
                </p>
                {n.recommendedNextWork.rationale && (
                  <p className="text-xs text-muted-foreground">
                    {n.recommendedNextWork.rationale}
                  </p>
                )}
                <Button
                  disabled={buildNext.isPending}
                  onClick={() => buildNext.mutate()}
                  data-testid="button-build-next"
                >
                  {buildNext.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Rocket className="h-4 w-4 mr-1.5" />
                  )}
                  Build Next Recommendation
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* How the work fared in review — counted, not described. */}
      <Card>
        <CardContent className="p-6">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Review
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="results-review-summary">
            <Stat label="Passed first time" value={results.reviewSummary.passedFirstTime} />
            <Stat label="Needed a revision" value={results.reviewSummary.revised} />
            <Stat label="Escalated to you" value={results.reviewSummary.escalated} />
            <Stat label="You approved" value={results.reviewSummary.userApproved} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Section({ title, body, testId }: { title: string; body: string; testId: string }) {
  if (!body) return null;
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {title}
      </div>
      <p className="text-sm mt-1.5 whitespace-pre-wrap" data-testid={testId}>
        {body}
      </p>
    </div>
  );
}

function ListSection({
  title,
  items,
  testId,
  icon: Icon = CheckCircle2,
}: {
  title: string;
  items: string[];
  testId: string;
  icon?: any;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {title}
      </div>
      <ul className="mt-1.5 space-y-1" data-testid={testId}>
        {items.map((item, i) => (
          <li key={i} className="text-sm flex items-start gap-2">
            <Icon className="h-3.5 w-3.5 mt-1 text-muted-foreground shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
